// Splits a takeaway sentence into a bold lead and the remainder. Display-only: the two
// halves concatenate back to the exact server string, so nothing is rewritten.

// Abbreviations whose trailing period is not a sentence end when a capitalised word
// follows ("vs. Acme", "Mr. Smith"). Dotted-letter forms (e.g., i.e., U.S., initials)
// are matched by pattern below instead.
const ABBREVIATIONS = new Set([
  "vs.", "mr.", "mrs.", "ms.", "dr.", "st.", "jr.", "sr.", "inc.", "ltd.", "co.", "corp.", "approx.", "etc.",
  "no.", "mt.", "fig.", "ave.",
]);

// Terminal punctuation, the whitespace after it, and a lookahead for an uppercase letter:
// a decimal ("3.5") has no whitespace, and a lowercase or digit continuation is not a new
// sentence.
const BOUNDARY = /([.!?]+)(\s+)(?=\p{Lu})/gu;

const DOTTED_LETTERS = /^(\p{L}\.)+$/u;

// A list number ("1.", "12.") is a marker, not a sentence. Only the full stop can end up
// as a boundary here: a closing bracket is not terminal punctuation.
const LIST_MARKER = /^\s*\d+\.$/;

/**
 * Whether the text before a period ends in an abbreviation rather than a sentence.
 * @param {string} head  Text up to and including the period.
 * @returns {boolean}
 */
const endsWithAbbreviation = (head) => {
  const token = (head.match(/(\S+)$/)?.[1] || "").replace(/^[("'“]+/, "").toLowerCase();
  return ABBREVIATIONS.has(token) || DOTTED_LETTERS.test(token);
};

const DASH = " — ";

/**
 * Where the first sentence of `text` ends, conservatively: just after the first `.`, `!` or
 * `?` that is followed by whitespace and then MORE text starting with an uppercase letter.
 * Decimals, abbreviations ("e.g.", "vs.", "Mr.", "No.", initials), a list number ("1.") and a
 * trailing sentence with nothing after it never end one. -1 when there is no such boundary.
 * @param {string} text
 * @returns {number}
 */
const sentenceEnd = (text) => {
  for (const m of text.matchAll(BOUNDARY)) {
    const end = m.index + m[1].length;
    const lead = text.slice(0, end);
    if (!/[\p{L}\p{N}]/u.test(lead)) continue;
    if (LIST_MARKER.test(lead)) continue;
    if (endsWithAbbreviation(lead)) continue;
    return end;
  }
  return -1;
};

/**
 * Split a takeaway into the bold lead and the rest, at whichever boundary comes FIRST:
 *  - an em dash set in spaces (" — "): the lead is everything before the first one, and the
 *    rest keeps the dash ("Two promises are past due — clear the reds." → lead "Two promises
 *    are past due"), the way the Clients list leads its rows; or
 *  - the end of the first sentence (see `sentenceEnd`): the lead includes its punctuation.
 * So "A. B — C" leads with "A." and "A — B. C" leads with "A". With neither boundary, or a
 * lead with no letter or digit in it, the result is null and the caller renders the text plain.
 *
 * `lead + rest === text` always (the whitespace after a sentence boundary stays in `rest`).
 *
 * @param {string} text
 * @returns {{lead: string, rest: string}|null}
 */
export const splitLeadSentence = (text) => {
  if (typeof text !== "string") return null;
  const dash = text.indexOf(DASH);
  const sentence = sentenceEnd(text);
  const end = dash !== -1 && (sentence === -1 || dash < sentence) ? dash : sentence;
  if (end === -1) return null;
  const lead = text.slice(0, end);
  if (!/[\p{L}\p{N}]/u.test(lead)) return null;
  return { lead, rest: text.slice(end) };
};
